import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {
  Address,
  RainbowResponseChannel,
} from '../../../../server/rainbow/types';
import {AvailabilityApiResponse} from '../../../../server/routes/availability/types';
import {getLanguageCode} from '../../../../shared/utils/getHeaderLanguageCode';
import {
  composeFetchApiRescheduleAvailability,
  composeFetchOrderVenue,
  composePostRescheduledOrder,
} from '../../../api/reschedule';
import {RootState} from '../../../hooks/store';

import {captureException} from '@treatwell/ui/datadog';
import {initialiseDatadogWithConfig} from '../../../datadog/initialiseClient';
import {fetchDateLocale} from '../../../../shared/utils/date';
import {getUrlInfo} from '../../../utils/getUrlInfo';
import {
  ApiAvailabilityAction,
  AvailabilityState,
  Status,
} from '../availability/slice';
import {fetchChannel} from '../channel/slice';
import {ANY_EMPLOYEE, PAST_RESCHEDULE_DATE} from '../constants';
import {isRequestPending} from '../selectors';
import {
  apiResponse,
  dateSelect,
  employeeMultiSelect,
  employeeSelect,
  startTimeSelect,
} from '../reducers';
import {RescheduleApiPostBody} from '../../../../server/routes/reschedule/types';
import {handleUnexpectedResourceAccess} from '../../../utils/handleUnexpectedResourceAccess';
import {requestConfig} from '../config/slice';
import {Config} from '../../../../shared/types';

export type RescheduledOrderState = {
  rescheduled: {
    status: Status;
    startDate: string | null;
    startTime: string | null;
  };
};

export type RescheduleOrderVenue = {
  venue: {
    status: Status;
    name?: string;
    address: Address;
    deposit?: {
      amount: string;
      remainderAmount: string;
    };
  };
};

type RescheduleApiError = {
  error: {
    type: typeof PAST_RESCHEDULE_DATE | null;
  };
};

const initialState: AvailabilityState &
  RescheduledOrderState &
  RescheduleApiError &
  RescheduleOrderVenue = {
  availabilityStatusRequest: 'idle',
  appStatusRequest: 'idle',
  paginationFutureStatusRequest: 'idle',
  error: {
    type: null,
  },
  data: {
    employees: {},
    days: {},
    services: [],
    skus: {},
    firstIncludedDate: '',
    lastIncludedDate: '',
    moreDaysInThePast: false,
    moreDaysInTheFuture: false,
    employeeSelectionAllowed: true,
  },
  selected: {
    initialDate: '',
    date: '',
    employee: ANY_EMPLOYEE,
    startTime: null,
    employeeMultiSelect: {},
    hasDiscount: false,
    totalPrice: '',
  },
  rescheduled: {
    status: 'idle',
    startDate: null,
    startTime: null,
  },
  venue: {
    status: 'idle',
    name: '',
    address: {
      postalCode: '',
      addressLines: [],
    },
  },
};

export const initialise = createAsyncThunk(
  'reschedule/initialise',
  ({url}: {url: string}, {dispatch, rejectWithValue}) => {
    const {searchParams} = getUrlInfo(url);
    const token = searchParams.get('token');

    if (!token) {
      return dispatch(fetchChannel({url}))
        .unwrap()
        .then(() => {
          throw new Error('missing token parameter');
        });
    }

    const requests: [
      Promise<RainbowResponseChannel>,
      Promise<AvailabilityApiResponse>,
      Promise<Config>,
    ] = [
      dispatch(fetchChannel({url})).unwrap(),
      dispatch(fetchApiRescheduleAvailability({url})).unwrap(),
      dispatch(requestConfig()).unwrap(),
    ];

    return Promise.all(requests)
      .then(([channelResponse]) => {
        const {locale, type} = channelResponse;

        if (type === 'widget') {
          return Promise.all([
            fetchDateLocale(locale),
            dispatch(fetchOrderVenue({token})).unwrap(),
          ]).then(() => {});
        }

        return fetchDateLocale(locale);
      })
      .catch((error) => {
        const isApiError = error.name === 'ApiError';
        return initialiseDatadogWithConfig().then(() => {
          if (!isApiError) {
            captureException(error, []);
          }
          try {
            const parsedError: {
              type: string;
              name: string;
              message: string;
            } = JSON.parse((error as Error).message);
            return rejectWithValue(parsedError.name);
          } catch {
            throw error;
          }
        });
      });
  },
  {
    condition(_, {getState}) {
      const state = getState() as RootState;
      return state.reschedule.appStatusRequest !== 'pending';
    },
  },
);

export const fetchApiRescheduleAvailability = createAsyncThunk(
  'reschedule/fetch',
  ({url, startDate = null}: ApiAvailabilityAction) => {
    const {searchParams, pathname} = getUrlInfo(url);
    const token = searchParams.get('token');

    const languageCode = getLanguageCode(pathname);
    const getApiRescheduleAvailability =
      composeFetchApiRescheduleAvailability();
    return getApiRescheduleAvailability({
      token,
      startDate,
      languageCode,
    });
  },
  {
    condition(_, {getState}) {
      const state = getState() as RootState;
      return isRequestPending(state);
    },
  },
);

export const postRescheduledOrder = createAsyncThunk(
  'reschedule/post',
  ({url, body}: {url: string; body: RescheduleApiPostBody}) => {
    const {searchParams} = getUrlInfo(url);
    const token = searchParams.get('token');

    const postRescheduledOrder = composePostRescheduledOrder();
    return postRescheduledOrder({
      token,
      body,
    });
  },
  {
    condition(_, {getState}) {
      const state = getState() as RootState;
      return state.reschedule.rescheduled.status !== 'pending';
    },
  },
);

export const fetchOrderVenue = createAsyncThunk(
  'reschedule/fetchOrderVenue',
  ({token}: {token: string}) => {
    const getOrderVenue = composeFetchOrderVenue();
    return getOrderVenue({
      token,
    });
  },
  {
    condition(_, {getState}) {
      const state = getState() as RootState;
      return state.reschedule.venue.status !== 'pending';
    },
  },
);

export const rescheduleSlice = createSlice({
  name: 'reschedule',
  initialState,
  reducers: {
    dateSelectReschedule: dateSelect,
    employeeSelectReschedule: employeeSelect,
    startTimeSelectReschedule: startTimeSelect,
    employeeMultiSelectReschedule: employeeMultiSelect,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchApiRescheduleAvailability.fulfilled, apiResponse);
    builder.addCase(fetchApiRescheduleAvailability.pending, (state, action) => {
      if (action.meta.arg.startDate) {
        state.paginationFutureStatusRequest = 'pending';
      }
      state.availabilityStatusRequest = 'pending';
    });
    builder.addCase(
      fetchApiRescheduleAvailability.rejected,
      (state, action) => {
        if (action.meta.arg.startDate) {
          if (action.error.message) {
            handleUnexpectedResourceAccess({
              errorMessage: action.error.message,
              onMatchedError: () => {
                // rainbow throws this error when we request a startDate that
                // does not exist in our database, which means there is no more
                // availability to paginate through. Therefore we set the pagination
                // status request to fulfilled so that the UI can hide the navigate
                // next arrow
                state.paginationFutureStatusRequest = 'fulfilled';
              },
              onAnyOtherError: () => {
                state.paginationFutureStatusRequest = 'rejected';
              },
            });
          }
        }
        state.availabilityStatusRequest = 'rejected';
      },
    );

    // this state is used to track when to render the entire app
    // all other actions and other dependencies (locale etc) will be
    // ready when this is 'fulfilled'
    builder.addCase(initialise.pending, (state) => {
      state.appStatusRequest = 'pending';
    });
    builder.addCase(initialise.rejected, (state, action) => {
      if (action.payload === PAST_RESCHEDULE_DATE) {
        state.error.type = PAST_RESCHEDULE_DATE;
      }
      state.appStatusRequest = 'rejected';
    });
    builder.addCase(initialise.fulfilled, (state) => {
      state.appStatusRequest = 'fulfilled';
    });

    builder.addCase(postRescheduledOrder.pending, (state) => {
      state.rescheduled.status = 'pending';
    });
    builder.addCase(postRescheduledOrder.rejected, (state) => {
      state.rescheduled.status = 'rejected';
    });
    builder.addCase(postRescheduledOrder.fulfilled, (state, action) => {
      state.rescheduled.startDate = action.payload.startDate;
      state.rescheduled.startTime = action.payload.startTime;
      state.rescheduled.status = 'fulfilled';
    });

    // getting venue information from the /me/order/ endpoint
    builder.addCase(fetchOrderVenue.pending, (state) => {
      state.venue.status = 'pending';
    });
    builder.addCase(fetchOrderVenue.rejected, (state) => {
      state.venue.status = 'rejected';
    });
    builder.addCase(fetchOrderVenue.fulfilled, (state, action) => {
      const venue = action.payload.items[0].data.venue;
      if (venue) {
        state.venue.name = venue.name;
        state.venue.address.postalCode = venue.location.address.postalCode;
        state.venue.address.addressLines = venue.location.address.addressLines;
      }
      state.venue.deposit = action.payload.deposit;

      state.venue.status = 'fulfilled';
    });
  },
});

export const {
  dateSelectReschedule,
  employeeSelectReschedule,
  startTimeSelectReschedule,
  employeeMultiSelectReschedule,
} = rescheduleSlice.actions;

export const selectRescheduleStatus = (state: RootState) => {
  return state.reschedule.rescheduled.status;
};

export const selectRescheduleStartTime = (state: RootState) => {
  return state.reschedule.rescheduled.startTime;
};

export const selectRescheduleStartDate = (state: RootState) => {
  return state.reschedule.rescheduled.startDate;
};

export const selectRescheduleErrorType = (state: RootState) => {
  return state.reschedule.error.type;
};
